/**
 * Onboarding Utilities
 *
 * Handles element targeting, position calculation, scrolling,
 * and viewport-aware tooltip placement.
 */

import { TooltipPlacement } from '@/types';

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_MARGIN = 12;
const TOOLTIP_MAX_WIDTH = 360;

function getTooltipMaxWidthMobile(): number {
  return Math.min(320, window.innerWidth - 24);
}

export interface ElementRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TooltipPosition {
  top: number;
  left: number;
  arrowDirection: 'up' | 'down' | 'left' | 'right' | 'none';
}

/**
 * Find an element by its data-tour attribute value.
 */
export function getElementByTourSelector(selector: string): HTMLElement | null {
  if (!selector) return null;
  return document.querySelector<HTMLElement>(selector);
}

/**
 * Get the bounding rect of an element with scroll-adjusted coordinates.
 */
export function getElementRect(el: HTMLElement): ElementRect {
  const rect = el.getBoundingClientRect();
  return {
    x: rect.left,
    y: rect.top,
    width: rect.width,
    height: rect.height,
  };
}

/**
 * Check if an element is visible in the viewport.
 */
export function isElementVisible(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * Scroll an element into view smoothly.
 */
export function scrollToElement(el: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    setTimeout(resolve, 400);
  });
}

/**
 * Wait for an element to appear in the DOM.
 * Returns the element or null if timeout is reached.
 */
export function waitForElement(
  selector: string,
  timeoutMs = 5000,
  intervalMs = 100
): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const check = () => {
      const el = getElementByTourSelector(selector);
      if (el && isElementVisible(el)) {
        resolve(el);
        return;
      }
      if (Date.now() - startTime >= timeoutMs) {
        resolve(null);
        return;
      }
      requestAnimationFrame(check);
    };

    // First check after a small delay to let DOM render
    setTimeout(check, intervalMs);
  });
}

/**
 * Check if we're on mobile viewport.
 */
export function isMobileViewport(): boolean {
  return window.innerWidth < 768;
}

/**
 * Get the spotlight rect (element rect + padding).
 */
export function getSpotlightRect(elementRect: ElementRect): ElementRect {
  return {
    x: elementRect.x - SPOTLIGHT_PADDING,
    y: elementRect.y - SPOTLIGHT_PADDING,
    width: elementRect.width + SPOTLIGHT_PADDING * 2,
    height: elementRect.height + SPOTLIGHT_PADDING * 2,
  };
}

/**
 * Calculate tooltip position based on target element and placement.
 * Adjusts to keep the tooltip within viewport bounds.
 * On mobile, left/right placements are converted to bottom/top since
 * there is not enough horizontal space on narrow screens.
 */
export function calculateTooltipPosition(
  elementRect: ElementRect,
  placement: TooltipPlacement,
  tooltipWidth: number,
  tooltipHeight: number,
): TooltipPosition {
  const isMobile = isMobileViewport();
  const maxWidth = isMobile ? getTooltipMaxWidthMobile() : TOOLTIP_MAX_WIDTH;
  const effectiveWidth = Math.min(tooltipWidth, maxWidth);
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const padding = isMobile ? 12 : 16;

  // Center placement (no target element)
  if (placement === 'center') {
    return {
      top: Math.max(padding, (vh - tooltipHeight) / 2),
      left: Math.max(padding, (vw - effectiveWidth) / 2),
      arrowDirection: 'none',
    };
  }

  // On mobile, convert horizontal placements to vertical (not enough space)
  let effectivePlacement = placement;
  if (isMobile && (placement === 'left' || placement === 'right')) {
    const elCenterY = elementRect.y + elementRect.height / 2;
    effectivePlacement = elCenterY < vh / 2 ? 'bottom' : 'top';
  }

  let top: number;
  let left: number;
  let arrowDirection: TooltipPosition['arrowDirection'];

  switch (effectivePlacement) {
    case 'bottom':
      top = elementRect.y + elementRect.height + TOOLTIP_MARGIN + SPOTLIGHT_PADDING;
      left = elementRect.x + elementRect.width / 2 - effectiveWidth / 2;
      arrowDirection = 'up';
      break;
    case 'top':
      top = elementRect.y - tooltipHeight - TOOLTIP_MARGIN - SPOTLIGHT_PADDING;
      left = elementRect.x + elementRect.width / 2 - effectiveWidth / 2;
      arrowDirection = 'down';
      break;
    case 'right':
      top = elementRect.y + elementRect.height / 2 - tooltipHeight / 2;
      left = elementRect.x + elementRect.width + TOOLTIP_MARGIN + SPOTLIGHT_PADDING;
      arrowDirection = 'left';
      break;
    case 'left':
      top = elementRect.y + elementRect.height / 2 - tooltipHeight / 2;
      left = elementRect.x - effectiveWidth - TOOLTIP_MARGIN - SPOTLIGHT_PADDING;
      arrowDirection = 'right';
      break;
    default:
      top = elementRect.y + elementRect.height + TOOLTIP_MARGIN;
      left = elementRect.x;
      arrowDirection = 'up';
  }

  // Horizontal flip for left/right when tooltip overflows
  if (effectivePlacement === 'left' && left < padding) {
    left = elementRect.x + elementRect.width + TOOLTIP_MARGIN + SPOTLIGHT_PADDING;
    arrowDirection = 'left';
  } else if (effectivePlacement === 'right' && left + effectiveWidth > vw - padding) {
    left = elementRect.x - effectiveWidth - TOOLTIP_MARGIN - SPOTLIGHT_PADDING;
    arrowDirection = 'right';
  }

  // Clamp to viewport
  if (left < padding) left = padding;
  if (left + effectiveWidth > vw - padding) left = vw - padding - effectiveWidth;
  if (top < padding) top = padding;
  if (top + tooltipHeight > vh - padding) top = vh - padding - tooltipHeight;

  // Vertical flip: if clamping puts tooltip behind the element
  if (effectivePlacement === 'bottom' && top < elementRect.y + elementRect.height) {
    top = elementRect.y - tooltipHeight - TOOLTIP_MARGIN - SPOTLIGHT_PADDING;
    arrowDirection = 'down';
    if (top < padding) top = padding;
  }
  if (effectivePlacement === 'top' && top + tooltipHeight > elementRect.y) {
    top = elementRect.y + elementRect.height + TOOLTIP_MARGIN + SPOTLIGHT_PADDING;
    arrowDirection = 'up';
    if (top + tooltipHeight > vh - padding) top = vh - padding - tooltipHeight;
  }

  return { top, left, arrowDirection };
}

/**
 * Resolve the route for a step, replacing :id with the actual project ID.
 */
export function resolveRoute(route: string, projectId?: string): string {
  if (route.includes(':id') && projectId) {
    return route.replace(':id', projectId);
  }
  return route;
}

/**
 * Check if the current URL matches a given route pattern.
 * When the pattern includes query params, they must also match.
 */
export function routeMatches(pattern: string, currentPath: string, projectId?: string): boolean {
  const resolvedPattern = resolveRoute(pattern, projectId);
  const [patternPath, patternQuery] = resolvedPattern.split('?');
  const [currentPathOnly, currentQuery] = currentPath.split('?');

  let pathMatch: boolean;
  if (patternPath.includes(':id')) {
    const prefix = patternPath.replace(':id', '');
    pathMatch = currentPathOnly.startsWith(prefix) && currentPathOnly.length > prefix.length;
  } else {
    pathMatch = patternPath === currentPathOnly;
  }

  if (!pathMatch) return false;

  // If pattern has query params, they must also match
  if (patternQuery) {
    return (currentQuery || '') === patternQuery;
  }
  return true;
}
